import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { MdClose, MdSave } from "react-icons/md";
import { createSectorKpi, updateSectorKpi } from "api/sectorKpis";

const SECONDARY = "#f27405";

export default function AddNewKPI({ open, onClose, onSuccess, sectorName, sectorId, role, editKpi = null }) {
  const isEdit = Boolean(editKpi?.id);
  const [form, setForm] = useState({
    name: "",
    description: "",
    target: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Pre-populate form when editing
  useEffect(() => {
    if (open) {
      if (editKpi) {
        setForm({
          name: editKpi.name || "",
          description: editKpi.definition || editKpi.description || "",
          target: editKpi.target != null ? String(editKpi.target) : (editKpi.target_value != null ? String(editKpi.target_value) : ""),
        });
      } else {
        setForm({ name: "", description: "", target: "" });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [open, editKpi]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (submitError) setSubmitError(null);
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "KPI name is required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const toastId = toast.loading(isEdit ? "Updating KPI…" : "Saving KPI...");

      if (isEdit) {
        // Update existing KPI
        const res = await updateSectorKpi(editKpi.id, {
          sector_id: sectorId,
          sector_name: sectorName || "",
          role: role || "operator",
          name: form.name.trim(),
          definition: form.description.trim() || "",
          target: form.target != null ? form.target.trim() : "",
        });
        const apiMessage = res?.message || res?.data?.message || "KPI updated";
        toast.success(apiMessage, { id: toastId });
        const payload = res?.data ?? res;
        if (typeof onSuccess === "function") onSuccess(payload);
      } else {
        // Create new KPI
        const res = await createSectorKpi({
          sector_id: Number(sectorId) || sectorId,
          sector_name: sectorName || "",
          role: role || "operator",
          name: form.name.trim(),
          definition: form.description.trim() || "",
          target: form.target != null ? form.target.trim() : "",
        });
        const apiMessage = res?.message || res?.data?.message || "KPI created";
        toast.success(apiMessage, { id: toastId });
        const payload = res?.data ?? res;
        if (typeof onSuccess === "function") onSuccess(payload);
      }
      setForm({ name: "", description: "", target: "" });
      setErrors({});
      onClose();
    } catch (err) {
      const message = err.response?.data?.message || err.message || `Failed to ${isEdit ? "update" : "create"} KPI`;
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setForm({ name: "", description: "" });
    setErrors({});
    setSubmitError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
        <div
          className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            <div>
              <h2 className="text-lg font-black text-slate-900 dark:text-white">
                {isEdit ? "Edit KPI" : "Add New KPI"}
              </h2>
              {(sectorName || role) && (
                <p className="text-xs text-slate-500 font-medium mt-0.5">
                  {sectorName && <>Sector: {sectorName}</>}
                  {sectorName && role && " · "}
                  {role && (
                    <span className="capitalize">Role: {role}</span>
                  )}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Close"
            >
              <MdClose className="text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div>
              <label
                htmlFor="add-kpi-name"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                KPI Name <span className="text-red-500">*</span>
              </label>
              <input
                id="add-kpi-name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Moisture Content (%)"
                className={`w-full rounded-xl border px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-offset-0 focus:outline-none ${
                  errors.name
                    ? "border-red-500 focus:ring-red-500/30"
                    : "border-slate-200 dark:border-slate-700 focus:ring-[#0e76a8]/30"
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-xs font-medium text-red-500">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="add-kpi-description"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                Definition
              </label>
              <textarea
                id="add-kpi-description"
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="e.g. Percentage of water in raw cashew nuts; ideal range is 8–10% to prevent fungal growth."
                rows={4}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:ring-offset-0 focus:outline-none resize-none"
              />
            </div>

            <div>
              <label
                htmlFor="add-kpi-target"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                Target
              </label>
              <input
                id="add-kpi-target"
                name="target"
                type="text"
                value={form.target}
                onChange={handleChange}
                placeholder="e.g. 10% or 5"
                className="w-full rounded-xl border px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:ring-offset-0 focus:outline-none"
              />
            </div>

            {submitError && (
              <p className="text-sm font-medium text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400 px-4 py-2 rounded-xl">
                {submitError}
              </p>
            )}

            {!isEdit && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-3">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                  Scoring range: 1–5
                </p>
                <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-0.5">
                  Score and status will be assessed after the KPI is created.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: SECONDARY }}
              >
                <MdSave className="text-lg" />
                {isSubmitting ? "Saving…" : isEdit ? "Update KPI" : "Save KPI"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
